/**
 * RightSideActionTracker — merged component
 *
 * Tabs:
 *   Left group  (workspace): Tasks | Notifications | Waiting For
 *   Right group (personal) : To Do | Meetings      | Emails
 *
 * Morning Briefing is controlled from the parent:
 *
 *   const [morningBriefing, setMorningBriefing] = useState(false);
 *
 *   // hide chat column when open
 *   <div className={morningBriefing ? "hidden" : "flex flex-1 h-full min-w-0"}>
 *     ...chat + resize handle...
 *   </div>
 *
 *   // right sidebar grows
 *   <div
 *     className="h-full overflow-hidden"
 *     style={{ width: morningBriefing ? "100%" : `${rightSidebarWidth}px` }}
 *   >
 *     <RightSideActionTracker
 *       isMorningBriefing={morningBriefing}
 *       onMorningBriefing={setMorningBriefing}
 *     />
 *   </div>
 */

import React, { useState, useRef, useEffect } from "react";

// ── Lucide (workspace tabs use these) ──
import {
  CheckCircle, XCircle, Loader2, PencilRuler, Ellipsis,
  ChevronUp, Filter, BrainCircuit, Bell, Hourglass, ListTodo,
  ChevronDown,
} from "lucide-react";

// ── react-icons (personal tabs use these) ──
import {
  FiCheck, FiMail, FiVideo, FiPlus, FiChevronDown,
  FiX, FiArrowRight, FiClock,
} from "react-icons/fi";
import { VscReport } from "react-icons/vsc";

// ── App contexts (workspace tabs) ──
import { DataState } from "../../context/DataProvider";
import TaskPlan from "./TaskPlan";
import { useTaskPlan } from "../../context/TaskPlanProvider";
import { RECENT_GOAL_COUNT, notifications as sampleNotifications } from "../../utils/constants";
import { appImages, waitingEvents } from "../../utils/constants";
import { useNotifications } from "../../context/NotificationProvider";
import { useEmails } from "../../context/EmailsProvider";
import NoMailScreen from "../dialog/NoMailScreen";
import EmailLabels from "../dialog/EmailLabels";
import { getGmailEmailDetailsFromDB, getOutlookEmailDetailsFromDB } from "../../services/api";


// ─────────────────────────────────────────────────────────────────────────────
// Static dummy data (personal tabs)
// ─────────────────────────────────────────────────────────────────────────────

const TODOS_INIT = [
  {
    id: 1,
    text: "Join Quick Meeting on Google Meet in 16 min",
    done: false,
    note: "I have a Quick Meeting starting in about 15 minutes on Google Meet. Help me think through what I might want to cover or any quick talking points I should prep before jumping in.",
  },
  { id: 2, text: "Do changes", done: false, note: "kasjd" },
  { id: 3, text: "Finalize investor deck slides", done: true, note: "" },
];

const SUGGESTIONS = [
  { id: 1, text: "Reply to Razorpay on OAuth vs Custom Onboarding" },
  { id: 2, text: "Decide on new Razorpay UPI credit instruments" },
  { id: 3, text: "Reply to Akshay about Hyderabad investor meeting" },
];

const MEETINGS = [
  {
    id: 1,
    title: "Technosport X SuperManager AGI",
    time: "10:00 AM – 10:30 AM",
    attendees: ["AN", "AD", "MA", "NI", "MZ"],
    colors: ["bg-violet-600", "bg-blue-600", "bg-emerald-600", "bg-amber-600", "bg-rose-600"],
    summary: "First demo with Technosport's D2C Lead, Anil Pradhan — a technically sharp operator. Nail the cross-team coordination and analytics angles.",
    emails: [
      "anil@technosport.in", "aditya46660@gmail.com",
      "manrozeoqlousagi@gmail.com", "nirmalnambiar@oqlous.com", "manroze@trysupermanager.com",
    ],
    prep: {
      who: "Anil Pradhan, D2C Lead at Technosport (since Sep 2024). Ex-Myntra Senior Manager CRM, ex-Udaan, ex-Apollo Pharmacy — 7+ years in digital growth, performance marketing, and retention. Technically strong: Python, SQL, data visualization.",
      about: "India's fast-growing performance activewear brand (think Indian Lululemon). Founded 2015, ~₹436 Cr revenue, Series A ($29.5M raised), 525+ employees. Just opened their 34th exclusive store.",
      fit: "Technosport's D2C operation means Anil's team juggles campaign launches, ecommerce ops, CRM workflows, logistics coordination — all across different tools. That cross-app chaos is exactly what SuperManager solves.",
      angles: [
        "Campaign/product launch coordination across teams (marketing, ops, logistics)",
        "Automated reporting and status updates instead of manual data pulls",
        "The Python/SQL guy will appreciate analytics and custom productivity charts",
        "Retail expansion coordination — 34 stores and growing means project management overhead",
      ],
      team: "Manroze (COO, organizer) and Aditya Sharma from Oqlous/SuperManager are joining you.",
      flow: "Brief intro → integrations list → live Jira/task demo → automated reporting → workflow builder → pricing. Ask about their current tool stack early.",
    },
  },
  {
    id: 2, title: "The House of Rare X SuperManager AGI", time: "11:00 AM – 11:30 AM",
    attendees: ["PU", "AD", "RK"], colors: ["bg-pink-600", "bg-blue-600", "bg-teal-600"],
    summary: "Follow-up demo with House of Rare's ops team. Focus on inventory and vendor coordination workflows.",
  },
  {
    id: 3, title: "Nirmal Nambiar and Tingyu Xiang", time: "12:30 PM – 12:40 PM",
    attendees: ["TI"], colors: ["bg-cyan-600"],
    summary: "Quick sync on integration timeline and API access.",
  },
  {
    id: 4, title: "Prince X Website", time: "3:00 PM – 4:00 PM",
    attendees: ["PR"], colors: ["bg-orange-600"],
    summary: "Website review and feedback session. Cover landing page, pricing section, and CTA copy.",
  },
];


// ─────────────────────────────────────────────────────────────────────────────
// Workspace tab helpers (from old code)
// ─────────────────────────────────────────────────────────────────────────────

function formatTimeAgo(dateInput) {
  if (!dateInput) return "";
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function generateMiscText(notification) {
  if (notification.source_type === "listener") {
    const payload = notification.listener_data.payload;
    switch (notification.listener_data.app) {
      case "jira": return `Ticket #${payload.ticketId}`;
      case "gmail": return `From: ${payload.from}`;
      case "zoho_crm": return `Lead: ${payload.leadName}, Value: $${payload.dealSize.toLocaleString()}`;
      case "slack": return `From: ${payload.user} in #${payload.channel}`;
      default: return `Source: ${notification.listener_data.app}`;
    }
  }
  if (notification.source_type === "workflow") return `Workflow: "${notification.workflow_data.workflow_title}"`;
  if (notification.source_type === "meeting") return `Subject: ${notification.subject}`;
  return "";
}

const NotificationItem = ({ notification, onClick }) => {
  const app = notification.source_type === "listener"
    ? notification.listener_data.app
    : notification.source_type === "meeting" ? notification.app : "agent";
  const miscText = generateMiscText(notification);
  return (
    <button onClick={onClick} className="flex items-start w-full gap-3 px-4 py-3 text-left transition-colors cursor-pointer hover:bg-zinc-800/60">
      <img src={appImages[app]} alt={app} className="h-5 w-5 flex-shrink-0 rounded mt-0.5" />
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium truncate text-zinc-200">{notification.event_name}</p>
          <p className="flex-shrink-0 ml-2 text-[10px] text-zinc-500">{formatTimeAgo(notification.timestamp)}</p>
        </div>
        <p className="text-[11px] text-zinc-400 truncate mt-0.5">{miscText}</p>
      </div>
    </button>
  );
};

const WaitingEventItem = ({ event }) => (
  <div className="flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer hover:bg-zinc-800/60">
    <img src={appImages[event.app]} alt={event.app} className="h-5 w-5 flex-shrink-0 rounded mt-0.5" />
    <div className="flex-1 overflow-hidden">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-zinc-200">{event.event}</p>
        <p className="flex-shrink-0 ml-2 text-[10px] text-zinc-500">{formatTimeAgo(event.date)}</p>
      </div>
      <p className="text-[11px] text-zinc-400 truncate mt-0.5">{event.misc}</p>
    </div>
  </div>
);

const WaitingForTask = () => (
  <div className="flex flex-col items-center justify-center flex-1 p-6 text-center text-zinc-500">
    <PencilRuler className="w-10 h-10 mb-4 text-zinc-600" strokeWidth={1.5} />
    <h4 className="mb-1 text-sm font-semibold text-zinc-400">Workspace Ready</h4>
    <p className="text-xs">Enter a task to see the agent's plan and actions here.</p>
  </div>
);

const ActionItem = ({ action, onActionClick }) => {
  let description = `Call ${action.function?.name}`;
  try {
    const args = JSON.parse(action.function?.arguments || "{}");
    description = args.action_description || description;
  } catch (e) { }

  const isRunning = action.status === "running";
  const getStatusDetails = () => {
    if (isRunning) {
      switch (action.running_phase || "calling_tool") {
        case "filtering": return { icon: <Filter className="w-4 h-4 text-cyan-400 animate-pulse" />, textColor: "text-cyan-300" };
        case "analyzing": return { icon: <BrainCircuit className="w-4 h-4 text-indigo-400" />, textColor: "text-indigo-300" };
        default: return { icon: <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />, textColor: "text-blue-300" };
      }
    }
    if (action.status === "success") return { icon: <CheckCircle className="w-4 h-4 text-emerald-400" />, textColor: "text-zinc-300" };
    if (action.status === "failed") return { icon: <XCircle className="w-4 h-4 text-red-400" />, textColor: "text-red-300" };
    return { icon: null, textColor: "text-zinc-400" };
  };

  const { icon, textColor } = getStatusDetails();
  return (
    <button onClick={() => onActionClick(action.id)} className="flex items-center w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-800/60">
      <div className="flex-shrink-0 w-4">{icon}</div>
      <p className={`flex-1 text-xs font-medium truncate ${textColor}`}>{description}</p>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Personal tab shared atoms
// ─────────────────────────────────────────────────────────────────────────────

function Av({ initials, color }) {
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold text-white shrink-0 ${color}`}>
      {initials}
    </span>
  );
}

function Label({ children }) {
  return <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{children}</p>;
}

// ─── Prep Modal ───────────────────────────────────────────────────────────────

function PrepModal({ meeting, onClose }) {
  if (!meeting?.prep) return null;
  const { who, about, fit, angles, team, flow } = meeting.prep;
  const sections = [
    ["Who you're meeting", <p key="w"><span className="font-semibold text-zinc-100">Anil Pradhan</span>, {who}</p>],
    ["What Technosport actually is", <p key="a">{about}</p>],
    ["Why SuperManager AGI fits", <p key="f">{fit}</p>],
    ["Angles that should land", (
      <ul key="ang" className="space-y-1.5">
        {angles.map((a, i) => (
          <li key={i} className="flex gap-2.5 items-start">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />{a}
          </li>
        ))}
      </ul>
    )],
    ["Your team on the call", <p key="t">{team}</p>],
    ["Standard demo flow", <p key="fl">{flow}</p>],
  ];
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm rounded-xl">
      <div className="relative w-full max-w-xl max-h-[90%] flex flex-col rounded-2xl border bg-zinc-900 border-zinc-700 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-800 shrink-0">
          <p className="text-sm font-semibold text-zinc-100">Meeting Prep</p>
          <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors">
            <FiX className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-4 text-sm text-zinc-300 leading-relaxed sc">
          {sections.map(([label, content]) => (
            <section key={label}>
              <Label>{label}</Label>
              <div className="mt-1.5">{content}</div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Email Panel (real data from useEmails) ───────────────────────────────────

// Priority badge helper — matches Dialog.jsx logic
function PriorityBadge({ priority }) {
  const isLow = priority <= 1;
  const isMed = priority <= 3;
  const label = isLow ? "Low" : isMed ? "Medium" : "High";
  const color = isLow ? "bg-[#5652CF]" : isMed ? "bg-yellow-500" : "bg-red-500";
  return (
    <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold text-white ${color}`}>
      {label}
    </span>
  );
}

// Inline expanded detail — summary + CTA
function EmailDetail({ email, onClose, onChatWithAgent }) {
  const subject  = email?.subject || "(no subject)";
  const from     = email?.from?.name || email?.from || "Unknown";
  const body     = email?.snippet || email?.bodyPreview || "";

  // Real fields from the email analysis pipeline————————————————
  const aiSummary   = email?.summary || null;
  const keyPoints   = [...(email?.criticalPoints || []), ...(email?.insights || [])];
  const [showAll, setShowAll] = useState(false);
  const visiblePoints = showAll ? keyPoints : keyPoints.slice(0, 2);
  const sentiment   = email?.sentiment || null;
  const tags        = email?.objectiveClass
    ? (Array.isArray(email.objectiveClass)
        ? email.objectiveClass
        : [email.objectiveClass])
    : [];

  // Fallback: build a quick snippet summary if no AI summary yet
  const displaySummary = aiSummary
    || `Email from ${from} — "${subject}". ${body.slice(0, 100)}${body.length > 100 ? "…" : ""}`;

  return (
    <div className="mx-3 mb-3 rounded-xl border border-zinc-700 bg-zinc-800/50 overflow-hidden animate-fi">
      {/* ── AI Summary ─────────────────────────────────────────── */}
      <div className="px-3 pt-3 pb-2 space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">AI Summary</p>
        <p className="text-xs text-zinc-300 leading-relaxed">{displaySummary}</p>

        {/* Key points */}
        {keyPoints.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Key Points</p>
            <ul className="space-y-1 pl-3">
              {visiblePoints.map((pt, i) => (
                <li key={i} className="flex gap-2 items-start text-[11px] text-zinc-400 leading-snug">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-zinc-500 shrink-0" />
                  {pt}
                </li>
              ))}
            </ul>
            {keyPoints.length > 2 && (
              <button
                onClick={() => setShowAll(p => !p)}
                className="mt-1 text-[10px] text-[#5652CF] hover:underline"
              >
                {showAll ? "Show less" : `+${keyPoints.length - 2} more`}
              </button>
            )}
          </div>
        )}

        {/* Sentiment + tags */}
        {(sentiment || tags.length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            {sentiment && (
              <span className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-zinc-700 text-zinc-300 border border-zinc-600">
                {sentiment}
              </span>
            )}
            {tags.map((tag, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-[#5652CF]/20 text-[#9b98f5] border border-[#5652CF]/30"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── actions ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-3 pb-3 pt-1 gap-2">
        <button
          onClick={() => onChatWithAgent(email)}
          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-[#5652CF] hover:bg-[#5652CF]/80 text-white text-xs font-medium transition-colors"
        >
          <FiMail className="w-3.5 h-3.5" />
          Chat with Agent
        </button>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function EmailPanel({ onStageChat }) {
  // ── real data ──
  const {
    emails,
    totalEmails,
    currentPage,
    setCurrentPage,
    emailfilter,
    setEmailFilter,
    loadingEmails,
    emailsPerPage,
    totalPages,
    isOutlook,
    isGmailAuthenticated,
    isOutlookAuthenticated,
    isGmailSynchronized,
    isOutlookSynchronized,
    emailStats,
  } = useEmails();

  const { userGoogleId } = DataState();

  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({}); // message_id → full email detail
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortKey, setSortKey] = useState("date"); // "date" | "sender"
  const filterRef = useRef(null);

  // Fetch full email detail (with summary, criticalPoints, insights…) on expand
  const fetchDetail = async (email) => {
    const msgId = email?.message_id || email?.id;
    if (!msgId || detailCache[msgId]) return; // already cached
    try {
      const fn = isOutlook ? getOutlookEmailDetailsFromDB : getGmailEmailDetailsFromDB;
      const detail = await fn(msgId, userGoogleId);
      if (detail) setDetailCache(prev => ({ ...prev, [msgId]: detail }));
    } catch (e) {
      console.warn("EmailPanel: detail fetch failed", e);
    }
  };

  // close filter on outside click
  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleFilterChange = (key, value) => {
    setEmailFilter(prev => {
      const next = { ...prev };
      if (key === "Inbox" || key === "SentItems") {
        delete next[key === "Inbox" ? "SentItems" : "Inbox"];
      }
      if (next[key] === value) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const handleChatWithAgent = (email) => {
    if (!onStageChat) return;
    const appLabel = isOutlook ? "microsoft_outlook" : "gmail";
    const payload = {
      _synthetic: true,           // not a real DB notification
      source_type: "listener",
      event_name: email?.subject || "Email",
      id: email?.id || String(Date.now()),
      listener_data: {
        app: appLabel,
        payload: {
          from:    email?.from?.address || email?.from || "",
          to:      email?.to || "",
          subject: email?.subject || "",
          body:    email?.bodyPreview || email?.snippet || "",
        },
      },
    };
    onStageChat(payload, payload.id);
  };

  const nextPage = () => setCurrentPage(p => Math.min(p + 1, totalPages));
  const prevPage = () => setCurrentPage(p => Math.max(p - 1, 1));

  // client-side sort only — EmailLabels drives filtering via setEmailFilter (server-side)
  const rawList = isOutlook
    ? (emails || []).map(e => ({ ...e, snippet: e.bodyPreview }))
    : (emails || []);

  const sorted = [...rawList].sort((a, b) => {
    if (sortKey === "sender") {
      const na = (a.from?.name || a.from || "");
      const nb = (b.from?.name || b.from || "");
      return na.localeCompare(nb);
    }
    return new Date(b.date) - new Date(a.date);
  });

  const isConnected = isOutlook ? isOutlookAuthenticated : isGmailAuthenticated;
  const isSynced = isOutlook ? isOutlookSynchronized : isGmailSynchronized;

  // ── not connected ──
  if (!isConnected) {
    return (
      <div className="flex flex-col flex-1 w-full h-full gap-2">
        <div className="flex flex-col items-center w-full h-full mx-auto overflow-hidden ">
          <NoMailScreen isOutlook={isOutlook} isGmailSynchronized={isGmailSynchronized} isOutlookSynchronized={isOutlookSynchronized} />
        </div>
      </div>
    );
  }

  // ── connected but not yet synced ──
  if (!isSynced) {
    return (
      <div className="flex flex-col flex-1 w-full h-full gap-2">
        <div className="flex flex-col items-center w-full h-full mx-auto overflow-hidden ">
          <NoMailScreen isOutlook={isOutlook} isGmailSynchronized={isGmailSynchronized} isOutlookSynchronized={isOutlookSynchronized} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">

      {/* ── toolbar ── */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 shrink-0">
        {/* inbox / sent pill */}
        <div className="flex items-center gap-1 bg-zinc-800/60 rounded-lg p-0.5">
          {[["Inbox", "Inbox"], ["SentItems", "Sent"]].map(([key, lbl]) => (
            <button key={key} onClick={() => handleFilterChange(key, key)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all
                ${emailfilter[key] ? "bg-zinc-700 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* filter icon — opens EmailLabels panel */}
        <div className="relative" ref={filterRef}>
          <button onClick={() => setFilterOpen(p => !p)}
            className={`p-1.5 rounded-lg transition-colors ${filterOpen ? "bg-zinc-700 text-zinc-100" : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800"}`}>
            <Filter className="w-3.5 h-3.5" />
          </button>

          {filterOpen && (
            <div className="absolute right-0 top-full mt-1 z-30 rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl p-3 animate-fi"
              style={{ minWidth: 210 }}>
              <EmailLabels
                emailStats={emailStats}
                emailfilter={emailfilter}
                setEmailFilter={setEmailFilter}
                handleFilterChange={handleFilterChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── list ── */}
      <div className="flex-1 overflow-y-auto sc">
        {loadingEmails ? (
          // skeleton
          <div className="divide-y divide-zinc-800/50">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3 animate-pulse">
                <div className="w-7 h-7 rounded-full bg-zinc-800 shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-zinc-800 rounded w-3/4" />
                  <div className="h-2.5 bg-zinc-800/60 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-center p-6">
            <FiMail className="w-7 h-7 text-zinc-600" />
            <p className="text-xs text-zinc-500">No emails found</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/50">
            {sorted.map((email, i) => {
              const id = email.id || i;
              const isOpen = expandedId === id;
              const from = email?.from?.name || email?.from || "Unknown";
              const subject = email?.subject || "(no subject)";
              const snippet = (email?.snippet || email?.bodyPreview || "").slice(0, 80);
              const date = email?.date
                ? new Date(email.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
                : "";

              return (
                <div key={id}>
                  <button
                    onClick={() => {
                      const newId = id === expandedId ? null : id;
                      setExpandedId(newId);
                      if (newId !== null) fetchDetail(email);
                    }}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors
                      ${isOpen ? "bg-zinc-800/60" : "hover:bg-zinc-800/40"}`}
                  >
                    {/* avatar */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0
                      ${["bg-violet-600", "bg-blue-600", "bg-emerald-600", "bg-amber-600", "bg-rose-600", "bg-cyan-600"][i % 6]}`}>
                      {from[0]?.toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-xs truncate ${(!email.isRead || emailfilter["SentItems"]) ? "font-semibold text-zinc-100" : "text-zinc-300"}`}>
                          {from.length > 20 ? from.slice(0, 20) + "…" : from}
                        </p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {email.priority !== undefined && <PriorityBadge priority={email.priority} />}
                          <span className="text-[10px] text-zinc-600">{date}</span>
                        </div>
                      </div>
                      <p className="text-[11px] text-zinc-400 truncate mt-0.5">{subject}</p>
                      {!isOpen && (
                        <p className="text-[10px] text-zinc-600 truncate mt-0.5">{snippet}</p>
                      )}
                    </div>

                    <FiChevronDown className={`w-3 h-3 text-zinc-600 shrink-0 mt-1 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isOpen && (() => {
                    const msgId = email?.message_id || email?.id;
                    const rich = (msgId && detailCache[msgId]) || email;
                    return (
                      <EmailDetail
                        email={rich}
                        onClose={() => setExpandedId(null)}
                        onChatWithAgent={handleChatWithAgent}
                      />
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── pagination ── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-zinc-800 shrink-0">
          <button onClick={prevPage} disabled={currentPage === 1}
            className="p-1 rounded text-zinc-500 hover:text-zinc-300 disabled:opacity-30 transition-colors">
            <FiChevronDown className="w-3.5 h-3.5 rotate-90" />
          </button>
          <span className="text-[10px] text-zinc-500 font-medium">
            {currentPage} / {totalPages}
          </span>
          <button onClick={nextPage} disabled={currentPage === totalPages}
            className="p-1 rounded text-zinc-500 hover:text-zinc-300 disabled:opacity-30 transition-colors">
            <FiChevronDown className="w-3.5 h-3.5 -rotate-90" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Meetings Panel ───────────────────────────────────────────────────────────

function MeetingsPanel() {
  const [expanded, setExpanded] = useState(null);
  const [prepMeeting, setPrepMeeting] = useState(null);
  return (
    <div className="relative flex-1 overflow-y-auto sc">
      <div className="divide-y divide-zinc-800/50">
        {MEETINGS.map(m => (
          <div key={m.id}>
            <button onClick={() => setExpanded(p => p === m.id ? null : m.id)}
              className="w-full px-4 py-3 text-left hover:bg-zinc-800/60 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2.5 min-w-0">
                  <FiVideo className="w-3.5 h-3.5 text-zinc-500 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-zinc-200 leading-snug truncate">{m.title}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{m.time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="flex -space-x-1.5">
                    {m.attendees.slice(0, 3).map((a, i) => <Av key={i} initials={a} color={m.colors[i]} />)}
                    {m.attendees.length > 3 && (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[9px] font-semibold bg-zinc-700 text-zinc-300">
                        +{m.attendees.length - 3}
                      </span>
                    )}
                  </div>
                  <FiChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform duration-200 ${expanded === m.id ? "rotate-180" : ""}`} />
                </div>
              </div>
            </button>
            {expanded === m.id && (
              <div className="px-4 pb-4 space-y-3 animate-fi">
                {m.summary && (
                  <div className="rounded-lg bg-zinc-800/60 p-3">
                    <Label>Meeting Summary</Label>
                    <p className="text-xs text-zinc-300 leading-relaxed mt-1.5">{m.summary}</p>
                  </div>
                )}
                {m.prep && (
                  <div className="flex items-center justify-between">
                    <Label>Meeting Prep</Label>
                    <button onClick={() => setPrepMeeting(m)}
                      className="px-3 py-1 text-[10px] font-medium rounded-full border border-zinc-600 text-zinc-300 hover:bg-zinc-700 hover:text-zinc-100 transition-colors">
                      Open
                    </button>
                  </div>
                )}
                {m.emails && (
                  <div className="space-y-1.5">
                    {m.emails.map((e, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Av initials={m.attendees[i] || "??"} color={m.colors[i] || "bg-zinc-600"} />
                        <span className="text-[11px] text-zinc-400 truncate">{e}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {prepMeeting && <PrepModal meeting={prepMeeting} onClose={() => setPrepMeeting(null)} />}
    </div>
  );
}

// ─── Todo Panel ───────────────────────────────────────────────────────────────

function TodoPanel({ compact = false }) {
  const [todos, setTodos] = useState(TODOS_INIT);
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [newNote, setNewNote] = useState("");

  const toggle = (id) => setTodos(p => p.map(t => t.id === id ? { ...t, done: !t.done } : t));
  const addSug = (s) => setTodos(p => [...p, { id: Date.now(), text: s.text, done: false, note: "" }]);
  const addNew = () => {
    if (newText.trim()) {
      setTodos(p => [...p, { id: Date.now(), text: newText.trim(), done: false, note: newNote.trim() }]);
      setNewText(""); setNewNote(""); setIsAdding(false);
    }
  };
  const cancelAdding = () => { setIsAdding(false); setNewText(""); setNewNote(""); };

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto sc">
      {/* Todos */}
      <div className="flex flex-col divide-y divide-zinc-800/50">
        {todos.map(t => (
          <div key={t.id} className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-zinc-800/60">
            <button onClick={() => toggle(t.id)}
              className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all
                ${t.done ? "bg-white/20 border-transparent text-white" : "border-zinc-500 hover:border-zinc-300"}`}>
              {t.done && <FiCheck className="w-3 h-3" strokeWidth={3} />}
            </button>
            <div className="min-w-0 flex-1">
              <p className={`text-xs font-medium truncate ${t.done ? "line-through text-zinc-500" : "text-zinc-200"}`}>{t.text}</p>
              {!compact && t.note && (
                <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{t.note}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add new todo */}
      <div className="flex items-start gap-3 px-4 py-3 border-b border-zinc-800/50">
        <div className="mt-0.5 w-4 h-4 rounded border border-zinc-600 shrink-0 opacity-50" />
        <div className="flex-1 flex flex-col min-w-0">
          <input value={newText} onChange={e => setNewText(e.target.value)}
            onFocus={() => setIsAdding(true)}
            onKeyDown={e => { if (e.key === "Enter") addNew(); if (e.key === "Escape") cancelAdding(); }}
            placeholder="Add new to do"
            className="text-xs bg-transparent outline-none text-zinc-200 placeholder-zinc-500 font-medium w-full" />
          {isAdding && (
            <div className="flex flex-col mt-1">
              <textarea value={newNote} onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addNew(); } if (e.key === "Escape") cancelAdding(); }}
                placeholder="Notes"
                className="text-[11px] bg-transparent outline-none text-zinc-400 placeholder-zinc-600 resize-none h-12 w-full mt-1" />
              <div className="flex items-center justify-between mt-2 border-t border-zinc-800/50 pt-2">
                <button className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400 transition-colors">
                  <VscReport className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={cancelAdding} className="text-[11px] text-zinc-500 hover:text-zinc-300 px-2 py-1">Cancel</button>
                  <button onClick={addNew} className="text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1 rounded transition-colors">Save</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Suggestions */}
      {!compact && (
        <div className="mt-6 mb-4 px-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500 flex items-center gap-2">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
                <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4" /><polyline points="14 2 14 8 20 8" />
                <path d="M2 15h10" /><path d="m9 18 3-3-3-3" />
              </svg>
              SUGGESTIONS
            </span>
          </div>
          <div className="flex flex-col">
            {SUGGESTIONS.map(s => (
              <div key={s.id} className="flex items-start gap-3 py-2 group cursor-pointer" onClick={() => addSug(s)}>
                <div className="mt-0.5 w-4 h-4 flex items-center justify-center shrink-0">
                  <FiPlus className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-300 transition-colors" />
                </div>
                <p className="text-xs font-medium text-zinc-400 leading-snug group-hover:text-zinc-200 transition-colors">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Morning Briefing view
// ─────────────────────────────────────────────────────────────────────────────

function MorningBriefingView({ onClose, onStageChat }) {
  const [activeTab, setActiveTab] = useState("emails");
  const briefingTabs = {
    emails: { icon: <FiMail className="w-3.5 h-3.5" />, label: "Emails" },
    meetings: { icon: <FiVideo className="w-3.5 h-3.5" />, label: "Meetings" },
  };

  return (
    <div className="flex h-full w-full overflow-hidden bg-1 rounded-xl border border-zinc-700 animate-si">
      {/* LEFT: greeting + todo */}
      <div
        className="relative flex flex-col border-r border-zinc-800 overflow-hidden"
        style={{ width: "60%", minWidth: 0, background: "linear-gradient(150deg, #18181b 0%, #27272a 55%, #1e1b4b 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none z-0"
          style={{ background: "radial-gradient(ellipse at 15% 65%, #6366f1 0%, transparent 60%)" }} />

        {/* hero */}
        <div className="relative z-10 px-7 pt-9 pb-7 shrink-0">
          <p className="text-[11px] text-zinc-500 font-medium tracking-wide mb-3">
            MARCH 29TH, 2026 &nbsp;·&nbsp; MATHURA 35°
          </p>
          <h1 className="text-[1.6rem] font-semibold text-zinc-100 leading-[1.3]">
            Good morning, <span className="text-white font-bold">Akshay.</span>
          </h1>
          <p className="text-lg text-zinc-300 font-normal mt-0.5 leading-snug">
            Clear Sunday,<br />
            <span className="text-zinc-400 font-light text-base">one thing carrying over.</span>
          </p>
          <button onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-xl text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/80 transition-colors z-10">
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* briefing text */}
        <div className="relative z-10 px-6 py-4 border-b border-zinc-800 shrink-0">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">☀ Morning Briefing</span>
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">
            No meetings today. The{" "}
            <span className="inline-flex items-center gap-1 align-middle">
              <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-blue-500 to-red-500 inline-block shrink-0 align-middle" />
              <span className="text-zinc-100 font-medium">investor meeting thread</span>
            </span>{" "}
            from <span className="text-zinc-100 font-medium">Akshay (akshayjangra225)</span>{" "}
            is still unread and marked urgent, asking about timing for the Hyderabad trip.
          </p>
          <p className="text-xs text-zinc-500 leading-relaxed mt-2">Pleasant 23°C evening in Mathura. Enjoy the rest of your weekend.</p>
        </div>

        {/* To Do label */}
        <div className="relative z-10 flex items-center gap-2 px-6 py-2.5 border-b border-zinc-800 shrink-0">
          <FiCheck className="w-3 h-3 text-zinc-500" />
          <Label>To Do</Label>
        </div>

        <div className="relative z-10 flex-1 min-h-0 overflow-hidden">
          <TodoPanel compact />
        </div>
      </div>

      {/* RIGHT: emails / meetings */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-zinc-800 shrink-0">
          <div>
            <p className="text-[10px] text-zinc-500">Catch Up</p>
            <h2 className="text-base font-semibold text-zinc-100 leading-tight">{briefingTabs[activeTab].label}</h2>
          </div>
          <div className="flex items-center gap-1 bg-zinc-800/60 rounded-xl p-1">
            {Object.entries(briefingTabs).map(([key, t]) => (
              <button key={key} onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                  ${activeTab === key ? "bg-zinc-700 text-zinc-100 shadow-sm" : "text-zinc-500 hover:text-zinc-300"}`}>
                {t.icon}<span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {activeTab === "emails" && <EmailPanel onStageChat={(...args) => { onClose(); onStageChat?.(...args); }} />}
          {activeTab === "meetings" && <MeetingsPanel />}
        </div>
        <div className="px-4 py-6 border-t border-zinc-800 shrink-0">
          <button onClick={onClose}
            className="flex items-center justify-center gap-3 w-full text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors group">
            <span>Finish Morning Briefing</span>
            <FiArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

const RightSideActionTracker = ({ isMorningBriefing = false, onMorningBriefing, onStageChat }) => {
  // ── Workspace context ──
  const {
    currentToolCallStatus,
    conversations,
    setScrollToToolId,
    creatingTask,
    isReasoning,
  } = DataState();
  const { plan, planStatus } = useTaskPlan();
  const {
    notifications,
    openNotificationInContext,
    setActiveNotificationId,
    removeCollapsedNotificationId,
  } = useNotifications();

  // ── Local state ──
  const [showAllGoals, setShowAllGoals] = useState(false);
  const [activeTab, setActiveTab] = useState("tasks"); // tasks | notifications | waitingEvents | todo | meetings | emails
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [isLastChatToolCalls, setIsLastChatToolCalls] = useState(false);
  useEffect(() => {
    conversations.forEach(c => {
      if (c?.role === "assistant") {
        setIsLastChatToolCalls(c?.tool_calls && Array.isArray(c?.tool_calls) && c?.tool_calls.length > 0);
      }
    });
  }, [conversations, currentToolCallStatus]);

  const isCurrent = isLastChatToolCalls;
  const successfulTasks = currentToolCallStatus.filter(a => a.status === "success").length;

  const handleNotificationClick = (notification) => {
    openNotificationInContext(notification.id);
    setActiveNotificationId(notification.id);
    removeCollapsedNotificationId(notification.id);
  };

  // ── Tab definitions ──
  // Workspace group
  const workspaceTabs = {
    tasks: { label: "Tasks", icon: <ListTodo className="w-3.5 h-3.5" /> },
    notifications: { label: "Notifications", icon: <Bell className="w-3.5 h-3.5" /> },
    waitingEvents: { label: "Waiting", icon: <FiClock className="w-3.5 h-3.5" /> },
  };
  // Personal group
  const personalTabs = {
    todo: { label: "To Do", icon: <FiCheck className="w-3.5 h-3.5" /> },
    meetings: { label: "Meetings", icon: <FiVideo className="w-3.5 h-3.5" /> },
    emails: { label: "Emails", icon: <FiMail className="w-3.5 h-3.5" /> },
  };

  const allTabs = { ...workspaceTabs, ...personalTabs };
  const isWorkspaceTab = (key) => key in workspaceTabs;
  const isPersonalTab = (key) => key in personalTabs;

  // ── Workspace content renderer ──
  const renderWorkspaceContent = () => {
    switch (activeTab) {
      case "notifications":
        return (
          <div className="flex-1 overflow-y-auto sc divide-y divide-zinc-800/50">
            {notifications.length === 0
              ? <p className="p-6 text-xs text-center italic text-zinc-500">No notifications yet.</p>
              : notifications.map(n => (
                <NotificationItem key={n.id} notification={n} onClick={() => handleNotificationClick(n)} />
              ))}
          </div>
        );
      case "waitingEvents":
        return (
          <div className="flex-1 overflow-y-auto sc divide-y divide-zinc-800/50">
            {waitingEvents.length === 0
              ? <p className="p-6 text-xs text-center italic text-zinc-500">Nothing waiting.</p>
              : waitingEvents.map(e => <WaitingEventItem key={e.id} event={e} />)}
          </div>
        );
      case "tasks":
      default:
        if (creatingTask) return <WaitingForTask />;
        return currentToolCallStatus.length > 0 ? (
          <div className="flex-1 overflow-y-auto sc divide-y divide-zinc-800/50">
            {currentToolCallStatus.map((tool, i) => (
              <ActionItem key={i} action={tool} onActionClick={setScrollToToolId} />
            ))}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center p-6 text-xs italic text-center text-zinc-500">
            No actions initiated yet.
          </div>
        );
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Morning Briefing mode
  // ─────────────────────────────────────────────────────────────────────────
  if (isMorningBriefing) {
    return (
      <>
        <div className="flex flex-col h-full w-full overflow-hidden">
          <MorningBriefingView onClose={() => onMorningBriefing?.(false)} onStageChat={onStageChat} />
        </div>
        <Styles />
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Normal sidebar mode
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="flex flex-col h-full w-full rounded-xl border border-zinc-700 bg-1 overflow-hidden">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 shrink-0">
          <div className="min-w-0">
            {/* <p className="text-[10px] text-zinc-500">Mathura 35°</p> */}
            <h1 className="text-lg font-semibold tracking-tight text-zinc-100 leading-tight truncate">
              {allTabs[activeTab]?.label ?? "Workspace"}
            </h1>
          </div>

          {/* Running badge (tasks only) */}
          {activeTab === "tasks" && !creatingTask && (isCurrent || planStatus !== "idle") && (
            <div className="flex items-center gap-2 px-2.5 py-1 border rounded-full bg-blue-900/50 border-blue-700/60 shrink-0 ml-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-blue-300">Running</span>
            </div>
          )}
        </div>

        {/* ── Tab bar: two groups separated by a divider ──────────────────── */}
        <div className="flex items-center gap-0.5 px-3 py-2 border-b border-zinc-800 shrink-0">
          {/* Workspace group */}
          {Object.entries(workspaceTabs).map(([key, t]) => (
            <button key={key} onClick={() => setActiveTab(key)} title={t.label}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all
                ${activeTab === key
                  ? "bg-zinc-700 text-zinc-100"
                  : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/60"
                }`}>
              {t.icon}
              {/* <span className="hidden lg:inline">{t.label}</span> */}
            </button>
          ))}

          {/* Divider */}
          <div className="w-px h-4 bg-zinc-700 mx-1.5 shrink-0" />

          {/* Personal group */}
          {Object.entries(personalTabs).map(([key, t]) => (
            <button key={key} onClick={() => setActiveTab(key)} title={t.label}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all
                ${activeTab === key
                  ? "bg-zinc-700 text-zinc-100"
                  : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/60"
                }`}>
              {t.icon}
              {/* <span className="hidden lg:inline">{t.label}</span> */}
            </button>
          ))}
        </div>

        {/* ── Task Plan (workspace: tasks tab only) ───────────────────────── */}
        {activeTab === "tasks" && !creatingTask && isReasoning && plan && plan.length > 0 && (
          <div className="shrink-0">
            <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-700">
              <div className="flex items-center py-1">
                {planStatus !== "idle" && (
                  <div className="flex items-center mr-4 space-x-1.5">
                    {[0, 0.3, 0.6].map((delay, i) => (
                      <div key={i} className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"
                        style={{ animationDelay: `${delay}s` }} />
                    ))}
                  </div>
                )}
                <h3 className="text-xs font-medium tracking-wider uppercase text-zinc-400">
                  {planStatus === "planning" ? "Planning" : planStatus === "analyzing" ? "Analyzing" : "Plan"}
                </h3>
              </div>
              {plan.length > RECENT_GOAL_COUNT && (
                <button onClick={() => setShowAllGoals(p => !p)} title={showAllGoals ? "Show recent" : "Show all"}
                  className="p-1 rounded-md text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100 transition-colors">
                  {showAllGoals ? <ChevronUp className="w-4 h-3" /> : <Ellipsis className="w-4 h-3" />}
                </button>
              )}
            </div>
            <TaskPlan showAll={showAllGoals} />
          </div>
        )}

        {/* ── Content area ────────────────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Workspace tabs */}
          {isWorkspaceTab(activeTab) && renderWorkspaceContent()}

          {/* Personal tabs */}
          {activeTab === "todo" && <TodoPanel />}
          {activeTab === "meetings" && <MeetingsPanel />}
          {activeTab === "emails" && <EmailPanel onStageChat={onStageChat} />}
        </div>

        {/* ── Footer bar (tasks count + morning briefing) ──────────────────── */}
        <div className="shrink-0 border-t border-zinc-800">
          {activeTab === "tasks" && !creatingTask && currentToolCallStatus.length > 0 && (
            <div className="flex justify-between px-4 py-2 text-[11px] text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              <span>{successfulTasks} completed</span>
              <span>{currentToolCallStatus.length} total</span>
            </div>
          )}
          <div className="px-4 py-3">
            <button onClick={() => onMorningBriefing?.(true)}
              className="flex items-center justify-center gap-3 w-full text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors group">
              <span>Morning Briefing</span>
              <FiArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      <Styles />
    </>
  );
};

function Styles() {
  return (
    <style>{`
      .sc::-webkit-scrollbar { width: 4px; }
      .sc::-webkit-scrollbar-track { background: transparent; }
      .sc::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 4px; }
      @keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
      .animate-fi { animation: fadeIn .15s ease-out; }
      @keyframes slideIn { from { opacity:0; transform:translateX(10px); } to { opacity:1; transform:translateX(0); } }
      .animate-si { animation: slideIn .2s ease-out; }
    `}</style>
  );
}

export default RightSideActionTracker;